import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import {
  CategoryIcon,
  Chip,
  EmptyState,
  IconSearch,
  ProductCard,
  SearchBar,
  Skeleton,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { api } from '../lib/api.js';
import { useAddToCart } from '../lib/use-add-to-cart.js';

/** Yozishni to'xtatgandan keyingi kutish — har harfda so'rov yuborilmaydi. */
const DEBOUNCE_MS = 300;

export function SearchScreen({ initialQuery = '' }: { initialQuery?: string }): ReactNode {
  const { t, locale } = useSettings();
  const { push } = useNavigation();
  const cart = useAddToCart();

  const [input, setInput] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(input.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [input]);

  const result = useQuery({
    queryKey: ['search', query],
    queryFn: ({ signal }) => api.search(query, signal),
    enabled: query.length > 0,
  });

  return (
    <div className="aps-screen">
      <SearchBar
        leading={<IconSearch size={18} />}
        value={input}
        onValueChange={setInput}
        placeholder={t('search.placeholder')}
        autoFocus
      />

      {query.length === 0 ? null : result.isPending ? (
        <div className="aps-grid-2">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} height="220px" />
          ))}
        </div>
      ) : (
        <>
          <div className="aps-row">
            {/* Imlo tuzatilgani MIJOZGA ko'rsatiladi — u nima
                qidirilganini bilishi kerak. */}
            {result.data?.correctedTo === null || result.data?.correctedTo === undefined ? null : (
              <Chip accent>
                {t('search.correctedTo')} {result.data.correctedTo}
              </Chip>
            )}
            {result.data?.degraded === true ? <Chip>{t('search.degraded')}</Chip> : null}
            <span className="aps-section-title">
              {result.data?.total ?? 0} {t('search.resultCount')}
            </span>
          </div>

          {(result.data?.items.length ?? 0) === 0 ? (
            <EmptyState title={t('search.empty')} hint={t('search.emptyHint')} />
          ) : (
            <div className="aps-grid-2">
              {(result.data?.items ?? []).map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  locale={locale}
                  fallbackIcon={<CategoryIcon iconKey={product.categoryIconKey} size={50} />}
                  onAddToCart={cart.add}
                  addToCartLabel={t('card.addToCart')}
                  addToCartDisabled={cart.disabled}
                  onSelect={() => {
                    push({ name: 'product', slug: product.slug });
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
